/**
 * J9 profession repeat loop (docs/design/2026-10-11-auto-play.md): `autoGather` repeats fishing, woodcut or a farm plot
 * harvest at the node the player stands by. Always the "ok" grade (no perfect, no resin, no rare fish), yield x0.7
 * (stochastic rounding from `h.random()`), budget at half rate (autoBudget.ts). Stops when the player is hit, moves,
 * the bag is full, the node is gone or the budget is empty.
 */
import { drainAutoBudget, newAutoBudget } from '../autoBudget';
import { BAG_SIZE } from '../data/items';
import { CROPS, NODE_KINDS } from '../data/professions';
import { plotRipe, rollYields } from '../professions';
import type { WorldEvent } from '../world';
import { CHOP_BEAT } from './chopBeat';
import { FISH_BITE } from './fishBite';
import type { SystemHost, WorldSystem } from './types';

export const AUTO_GATHER = { yield: 0.7, budgetRate: 0.5, moveEps: 0.5, farmCycle: 3 } as const;
export type AutoGatherMode = 'fish' | 'chop' | 'farm';
export type AutoGatherCommand = { t: 'autoGather'; on: boolean };
export type AutoGatherEvent = { type: 'autoGather'; on: boolean; mode?: AutoGatherMode; reason?: string };

export class AutoGatherSystem implements WorldSystem {
  readonly name = 'autoGather';
  mode: AutoGatherMode | null = null;
  private ax = 0;
  private ay = 0;
  private hp = 0;
  private t = 0;
  private cycle = 0;
  constructor(private readonly h: SystemHost) {}

  readonly commands = {
    autoGather: (c: AutoGatherCommand) => {
      if (c.on) this.start();
      else this.stop('off');
    },
  };

  private ev(e: AutoGatherEvent): void {
    this.h.emit(e as unknown as WorldEvent);
  }

  private modeAt(): AutoGatherMode | null {
    const w = this.h.world;
    const n = w.trades.nodeNear(w.player);
    if (!n) return null;
    if (n.kind.id === 'fish-spot') return 'fish';
    if (n.kind.terrain === 'tree') return 'chop';
    if (n.kind.id === 'plot' && w.trades.plot(n.id)) return 'farm';
    return null;
  }

  start(): boolean {
    const w = this.h.world;
    const mode = this.modeAt();
    if (!mode || w.player.dead) return false;
    const b = (w.progress.autoBudget ??= newAutoBudget(0));
    if (b.secs <= 0) {
      this.h.notice('เวลาออโต้หมด รอรีเซ็ตตอน 05:00');
      return false;
    }
    this.mode = mode;
    this.ax = w.player.x;
    this.ay = w.player.y;
    this.hp = w.player.hp;
    this.t = 0;
    this.cycle = this.cycleTime(mode);
    this.ev({ type: 'autoGather', on: true, mode });
    return true;
  }

  stop(reason: string): void {
    if (!this.mode) return;
    this.mode = null;
    this.ev({ type: 'autoGather', on: false, reason });
  }

  private cycleTime(mode: AutoGatherMode): number {
    if (mode === 'fish') return FISH_BITE.waitMin + this.h.random() * (FISH_BITE.waitMax - FISH_BITE.waitMin) + FISH_BITE.window / 2;
    if (mode === 'chop') return CHOP_BEAT.hits * CHOP_BEAT.period;
    return AUTO_GATHER.farmCycle;
  }

  /** n x 0.7 with the fraction rolled from the host's Rng. */
  private scaled(n: number): number {
    const v = n * AUTO_GATHER.yield;
    const f = Math.floor(v);
    return f + (this.h.random() < v - f ? 1 : 0);
  }

  /** Gives goods x0.7; false when the bag took nothing of a non-empty haul (full). */
  private give(goods: { id: string; n: number }[]): { id: string; n: number }[] | null {
    const t = this.h.world.trades;
    const got: { id: string; n: number }[] = [];
    let want = 0;
    for (const g of goods) {
      const n = this.scaled(g.n);
      if (n <= 0) continue;
      want++;
      const add = t.give(g.id, n);
      if (add > 0) got.push({ id: g.id, n: add });
    }
    return want > 0 && got.length === 0 ? null : got;
  }

  private doCycle(mode: AutoGatherMode): boolean {
    const w = this.h.world;
    const t = w.trades;
    if (mode === 'fish') {
      const goods: { id: string; n: number }[] = [];
      for (const y of NODE_KINDS['fish-spot']!.yields) {
        if (y.chance < 1) continue; // never rare
        goods.push({ id: y.id, n: y.min + Math.floor(this.h.random() * (y.max - y.min + 1)) });
      }
      const got = this.give(goods);
      if (!got) return false;
      this.ev({ type: 'fishBite', phase: 'result', result: 'catch', goods: got } as unknown as AutoGatherEvent);
      return true;
    }
    if (mode === 'chop') {
      const got = this.give([{ id: 'pine-log', n: CHOP_BEAT.hits }]);
      if (!got) return false;
      this.ev({ type: 'chopBeat', onBeat: true, log: got[0]?.n ?? 0, resin: 0, done: true } as unknown as AutoGatherEvent);
      return true;
    }
    const n = t.nodeNear(w.player);
    if (!n || n.kind.id !== 'plot') return false;
    const p = t.plot(n.id);
    const crop = p && CROPS[p.crop];
    if (!p || !crop) return false;
    if (!plotRipe(p)) return true; // not ripe yet: wait another cycle
    delete w.progress.trades.plots[n.id];
    const goods = rollYields(crop.yields, t.level('farmer'), crop.level, () => this.h.random());
    return this.give(goods) !== null;
  }

  step(dt: number): void {
    const mode = this.mode;
    if (!mode) return;
    const w = this.h.world;
    const p = w.player;
    if (p.dead || p.hp < this.hp - 1e-6) return this.stop('hit');
    this.hp = p.hp;
    if (Math.hypot(p.x - this.ax, p.y - this.ay) > AUTO_GATHER.moveEps) return this.stop('move');
    if (w.progress.inventory.length >= BAG_SIZE) return this.stop('full');
    const b = (w.progress.autoBudget ??= newAutoBudget(0));
    if (drainAutoBudget(b, dt * AUTO_GATHER.budgetRate) <= 0) return this.stop('budget');
    this.t += dt;
    if (this.t < this.cycle) return;
    this.t -= this.cycle;
    if (!this.doCycle(mode)) return this.stop('full');
    this.cycle = this.cycleTime(mode);
  }
}
